import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BotsActivityBreakdownList, {
  BotsActivityBreakdownListRow,
} from '../BotsActivityBreakdownList';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { BotsActivityContentRow } from '../../../../shared/types/bots-activity/BotsActivityContentResponse';
import { BotsActivityBreakdownEntry } from '../../../../shared/types/bots-activity/BotsActivityBreakdownEntry';
import { getBotColor } from '../../../../shared/constants/botsActivityColors';
import { getPlatformIcon, getBotIcon } from '../../../../shared/constants/botsActivityIcons';
import { presentContentPath, type ContentPathContext } from './contentPathPresenter';

interface BotsActivityContentPanelProps {
  topFolders: BotsActivityContentRow[];
  topUrls: BotsActivityContentRow[];
  /**
   * Visits to root-level config files (robots.txt, sitemap.xml, …) intentionally
   * excluded from the "By folder" panel. Surfaced as a small footnote so the
   * user knows where that traffic went (it's in "By URL").
   */
  excludedFileVisits?: number;
  byPlatform: BotsActivityBreakdownEntry[];
  byBot: BotsActivityBreakdownEntry[];
  byBotType: BotsActivityBreakdownEntry[];
}

const toContentRows = (
  rows: BotsActivityContentRow[],
  context: ContentPathContext,
  homepageLabel: string
): BotsActivityBreakdownListRow[] =>
  rows.map((r) => {
    const { label, secondaryLabel, iconClass, title } = presentContentPath(
      r.path,
      context,
      homepageLabel
    );
    return {
      label,
      secondaryLabel,
      iconClass,
      title,
      count: r.count,
      changePct: r.changePct ?? null,
      lastSeenAt: r.lastSeenAt ?? null,
    };
  });

export const BotsActivityContentPanel: React.FC<BotsActivityContentPanelProps> = ({
  topFolders,
  topUrls,
  excludedFileVisits,
  byPlatform,
  byBot,
  byBotType,
}) => {
  const { t } = useTranslation();
  const homepageLabel = t('botsActivity.homepage');
  const hasFolderRows = topFolders.length > 0;
  const filesExcluded = (excludedFileVisits ?? 0) > 0;

  const folderRows = useMemo(
    () => toContentRows(topFolders, 'folder', homepageLabel),
    [topFolders, homepageLabel]
  );

  const urlRows = useMemo(
    () => toContentRows(topUrls, 'url', homepageLabel),
    [topUrls, homepageLabel]
  );

  const platformRows: BotsActivityBreakdownListRow[] = useMemo(
    () =>
      byPlatform.map((row) => ({
        label: row.label,
        count: row.count,
        iconClass: getPlatformIcon(row.label),
        color: getBotColor(row.label),
      })),
    [byPlatform]
  );

  const botRows: BotsActivityBreakdownListRow[] = useMemo(
    () =>
      byBot.map((row) => ({
        label: row.label,
        count: row.count,
        iconClass: getBotIcon(row.label),
        color: getBotColor(row.label),
      })),
    [byBot]
  );

  // Bot-type rows mirror By platform / By bot — same horizontal-bar list layout per
  // category (User query / Training / Search). The aggregation hook already attaches the
  // brand color (from `getBotColor`) so we just pass it through; counts are raw, the
  // BreakdownList scales bar widths to the row max.
  const botTypeRows: BotsActivityBreakdownListRow[] = useMemo(
    () =>
      byBotType.map((row) => ({
        label: row.label,
        count: row.count,
        color: row.color,
      })),
    [byBotType]
  );

  return (
    <>
      <div className="stats__panel">
        <div className="page-box__title-action">
          <div className="page-box__title page-box__title--flex">
            {t('botsActivity.content')}
            <CustomTooltip tooltipText="botsActivity.contentActivityHint" />
          </div>
        </div>
        <div className="bots-activity__grid bots-activity__grid--2">
          <div className="bots-activity__sub-panel">
            {hasFolderRows ? (
              <BotsActivityBreakdownList
                rows={folderRows}
                title={t('botsActivity.byFolder')}
                titleIconClass="ico--folder"
                tooltipKey="botsActivity.bySectionHint"
                showMetaColumns={true}
                initialLimit={10}
              />
            ) : filesExcluded ? (
              <div className="breakdown-list breakdown-list--empty">
                <div className="breakdown-list__header">
                  <span className="ico--folder ico--sm breakdown-list__header-icon" aria-hidden="true" />
                  <span className="breakdown-list__header-title">{t('botsActivity.byFolder')}</span>
                </div>
                <div className="bots-activity__panel-empty">
                  {t('botsActivity.byFolderEmptyFilesOnly')}
                </div>
              </div>
            ) : (
              <BotsActivityBreakdownList
                rows={[]}
                title={t('botsActivity.byFolder')}
                titleIconClass="ico--folder"
                tooltipKey="botsActivity.bySectionHint"
                showMetaColumns={true}
                initialLimit={10}
              />
            )}
            {hasFolderRows && filesExcluded && (
              <div className="bots-activity__panel-footnote">
                {t('botsActivity.byFolderExcludedHint', {
                  count: excludedFileVisits ?? 0,
                })}
              </div>
            )}
          </div>
          <div className="bots-activity__sub-panel">
            <BotsActivityBreakdownList
              rows={urlRows}
              title={t('botsActivity.byUrl')}
              titleIconClass="ico--globe"
              tooltipKey="botsActivity.topUrlsHint"
              showMetaColumns={true}
              initialLimit={10}
            />
          </div>
        </div>
      </div>

      <div className="stats__panel">
        <div className="page-box__title-action">
          <div className="page-box__title page-box__title--flex">
            {t('botsActivity.crawlBreakdown')}
            <CustomTooltip tooltipText="botsActivity.breakdownHint" />
          </div>
        </div>
        <div className="bots-activity__grid bots-activity__grid--breakdown">
          <div className="bots-activity__sub-panel">
            <BotsActivityBreakdownList
              rows={platformRows}
              title={t('botsActivity.byPlatform')}
              titleIconClass="ico--bot"
              iconSize="md"
              initialLimit={10}
            />
          </div>
          <div className="bots-activity__sub-panel">
            <BotsActivityBreakdownList
              rows={botRows}
              title={t('botsActivity.byBot')}
              titleIconClass="ico--bot"
              iconSize="md"
              initialLimit={10}
            />
          </div>
          <div className="bots-activity__sub-panel">
            <BotsActivityBreakdownList
              rows={botTypeRows}
              title={t('botsActivity.byBotType')}
              titleIconClass="ico--stats"
              iconSize="md"
              initialLimit={10}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BotsActivityContentPanel;
